/**
 * How much of the app's furniture is showing while you scroll a grid of cards.
 *
 * The owner's ask: "scrolling down just make a little room by hiding the search
 * bar and organiser AND the bottom nav goes like an animation shrinks to the
 * left out of the way into a ball and its a button that says 'menu' and maybe
 * that bottom nav has a trigger that is like a few paces down."
 *
 * So it is two triggers, not one, and that is what keeps it from feeling
 * twitchy. The search bar and the sort control go early, because they are cheap
 * to get back and nobody is reading them mid-scroll. The bottom nav only goes
 * much later, because it is the way off the screen and losing it by accident is
 * a different order of annoyance.
 *
 * Three states, in order of how much is left:
 *
 *   full       everything showing. Where every screen starts.
 *   hidden     the search bar and the sort control are gone. The nav is not.
 *   collapsed  the nav has shrunk into the Menu ball as well.
 *
 * The state lives here rather than in a component because the two halves of it
 * are rendered in two different trees: the chrome is inside the page, and the
 * bottom nav is a sibling of every page over in App.js. Same module-level
 * subscribe pattern the TCG Pocket set list uses.
 *
 * It is deliberately NOT persisted. A collapsed nav is an artefact of where you
 * happen to be scrolled, not a preference, so leaving the screen and coming
 * back starts at `full` again.
 */
import { useEffect, useState } from 'react';

export const CHROME_FULL = 'full';
export const CHROME_HIDDEN = 'hidden';
export const CHROME_COLLAPSED = 'collapsed';

/**
 * How far down before each stage.
 *
 * Chosen by scrolling the real grid rather than by arithmetic. 140px is a bit
 * under one row of cards, so the chrome goes as soon as you have committed to
 * moving rather than on a nudge. 900px is past three rows, which is not
 * somewhere you arrive by accident.
 */
// The toolbar is no longer hidden by anything: it sits inside the scrolling
// content and scrolls away on its own, which is what removed the jump. This
// stage is kept only as the halfway point on the way to collapsing the nav, so
// the two do not happen on the same frame.
const HIDE_AT = 140;
const COLLAPSE_AT = 900;
/** Back to the very top, with a little slack for rubber-banding. */
const RESTORE_AT = 8;

/** How much furniture each state leaves. Higher means less. */
const RANK = { [CHROME_FULL]: 0, [CHROME_HIDDEN]: 1, [CHROME_COLLAPSED]: 2 };

let state = CHROME_FULL;
const listeners = new Set();

function publish(next) {
  if (next === state) return;
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function getChromeState() {
  return state;
}

/** Put everything back. The Menu ball, and leaving the screen. */
export function resetChrome() {
  publish(CHROME_FULL);
}

/**
 * Feed a scroll position in and let it decide.
 *
 * Deliberately NOT based on which way the finger went, which is the obvious
 * implementation and the wrong one. A smooth scroll fires events a few pixels
 * apart, so any noise floor big enough to ignore jitter is also big enough to
 * swallow every real event, and a floor small enough to catch them flaps on the
 * jitter instead.
 *
 * Direction is not needed anyway, because the behaviour asked for is monotonic.
 * The rules reduce to two:
 *
 *   Going down can only ever take MORE away, never give it back. That is what
 *   makes scrolling up mid-list leave things alone - there is no rule that
 *   restores anything, so nudging up to re-check a card cannot flap the header
 *   back in.
 *
 *   Reaching the very top gives everything back, because someone who scrolled
 *   back to the start staring at a list with no search bar and no sort control
 *   is not in a considered state, just a stuck one.
 *
 * Momentum falls out for free. An iOS flick keeps firing after the finger
 * lifts; every one of those events re-derives the same state from the same
 * absolute position, and publishing a value equal to the current one is a
 * no-op, so an overshoot that settles cannot toggle anything twice.
 */
export function onScrollPosition(top) {
  if (top <= RESTORE_AT) {
    publish(CHROME_FULL);
    return;
  }
  let next = CHROME_FULL;
  if (top > COLLAPSE_AT) next = CHROME_COLLAPSED;
  else if (top > HIDE_AT) next = CHROME_HIDDEN;
  if (RANK[next] > RANK[state]) publish(next);
}

/** Subscribe a component to the current state. */
export function useChromeState() {
  const [value, setValue] = useState(state);
  useEffect(() => {
    // Re-read on mount: a component that mounts late would otherwise miss the
    // publish that already happened.
    setValue(state);
    listeners.add(setValue);
    return () => { listeners.delete(setValue); };
  }, []);
  return value;
}

/**
 * Does this person want motion kept to a minimum?
 *
 * Read live rather than cached, because the setting can change while the app is
 * open. Callers use it to swap the transition for nothing at all - the state
 * change still happens, it just happens instantly, which is the point of the
 * setting. A faster animation is still an animation.
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
